import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Token storage
const getAccessToken = () => localStorage.getItem('access_token');
const getRefreshToken = () => localStorage.getItem('refresh_token');
const setTokens = (access, refresh) => {
  if (access) localStorage.setItem('access_token', access);
  if (refresh) localStorage.setItem('refresh_token', refresh);
};
const clearTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

// Single request interceptor - add token
axios.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Single response interceptor - refresh on 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || '';
    
    // Never intercept auth endpoints
    if (url.includes('/auth/')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        isRefreshing = false;
        clearTokens();
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {
          refresh_token: refreshToken
        });
        setTokens(data.access_token, null);
        processQueue(null, data.access_token);
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        clearTokens();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }

    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      setUser(data);
    } catch (e) {
      // Auth check failed (expired/invalid token) - clear and show login
      clearTokens();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    if (!data.access_token) {
      throw new Error('Resposta do servidor sem token de acesso');
    }
    setTokens(data.access_token, data.refresh_token);
    await checkAuth();
  };

  const register = async (email, password, name, businessType, businessName, enabledModules, language, country, state, city, currency, businessSegment, accountType) => {
    const { data } = await axios.post(`${API_URL}/api/auth/register`, {
      email, password, name,
      business_type: businessType || "autonomo",
      account_type: accountType || null,
      business_name: businessName || "",
      business_segment: businessSegment || "",
      enabled_modules: enabledModules || [],
      language: language || "pt-BR",
      country: country || "",
      state: state || "",
      city: city || "",
      currency: currency || "BRL",
    });
    if (!data.access_token) {
      throw new Error('Resposta do servidor sem token de acesso');
    }
    setTokens(data.access_token, data.refresh_token);
    await checkAuth();
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch (err) {
      console.warn('Logout request failed:', err.message);
    }
    try {
      await axios.post(`${API_URL}/api/auth/google-logout`, {});
    } catch { /* Google logout may fail if not a Google user */ }
    clearTokens();
    setUser(false);
  };

  const value = useMemo(() => ({ user, loading, login, register, logout, checkAuth }), [user, loading, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
